/**
 * Dashboard: the professional-terminal-style home screen. Hosts every
 * widget as an independently-loading tile so one slow/failing data
 * source never blocks the rest of the dashboard from rendering.
 */

import React from "react";
import { useAuth } from "@/context/AuthContext";
import APIHealthWidget from "@/components/dashboard/APIHealthWidget";
import EconomicCalendarWidget from "@/components/dashboard/EconomicCalendarWidget";
import ForecastSummaryWidget from "@/components/dashboard/ForecastSummaryWidget";
import LatestNewsWidget from "@/components/dashboard/LatestNewsWidget";
import LearningProgressWidget from "@/components/dashboard/LearningProgressWidget";
import MarketStatusWidget from "@/components/dashboard/MarketStatusWidget";
import PortfolioRiskChart from "@/components/dashboard/PortfolioRiskChart";
import PortfolioValueWidget from "@/components/dashboard/PortfolioValueWidget";
import ProviderStatusWidget from "@/components/dashboard/ProviderStatusWidget";
import RiskSummaryWidget from "@/components/dashboard/RiskSummaryWidget";
import SchedulerStatusWidget from "@/components/dashboard/SchedulerStatusWidget";
import SectorHeatmapWidget from "@/components/dashboard/SectorHeatmapWidget";
import TopResearchPicksWidget from "@/components/dashboard/TopResearchPicksWidget";
import SyncTickerPanel from "@/components/research/SyncTickerPanel";

export default function Dashboard(): React.ReactElement {
  const { user } = useAuth();

  return (
    <div className="space-y-6 stagger-children">
      <div>
        <h1 className="text-2xl font-semibold">
          Welcome back{user ? `, ${user.full_name}` : ""}
        </h1>
        <p className="mt-1 text-sm text-mist-400">
          Here's the current state of your research workspace.
        </p>
      </div>

      <SyncTickerPanel />

      <div className="grid stagger-children grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <PortfolioValueWidget />
        <MarketStatusWidget />
        <RiskSummaryWidget />
        <APIHealthWidget />
      </div>

      <PortfolioRiskChart />

      <div className="grid stagger-children grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4">
        <TopResearchPicksWidget />
        <ForecastSummaryWidget />
        <LatestNewsWidget />
        <EconomicCalendarWidget />
      </div>

      <div className="grid stagger-children grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SectorHeatmapWidget />
        <LearningProgressWidget />
        <SchedulerStatusWidget />
        <ProviderStatusWidget />
      </div>
    </div>
  );
}
